import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { ModelUnitEdit, ModelUnitAdd } from "./ModelUnitAddOrEdit";
import EncryptionUtils from "../../Utils/EncryptionUtils";

export default function AddOrEditModelUnit({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [modelUnitData, setModelUnitData] = useState(new ModelUnitAdd());
  const [errors, setErrors] = useState({});
  const [marcas, setMarcas] = useState([]);

  useEffect(() => {
    if (location.pathname.includes("/ModelosUnidad/Editar")) {
      setIsEditMode(true);
      const modelUnitId = getModelUnitIdFromPath(location.pathname);
      getModelUnitById(modelUnitId);
    }

    getMarcas();
  }, [location.pathname]);

  const getModelUnitIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getModelUnitById = async (modelUnitId) => {
    const dataModelUnit = await GET(
      `${config.url}/api/ModelUnit/${modelUnitId}`
    );
    const updateDataModelUnit = new ModelUnitEdit(dataModelUnit);

    setModelUnitData(updateDataModelUnit);
  };

  const getMarcas = async () => {
    const dataMarca = await GET(`${config.url}/api/Marcas`);

    setMarcas(dataMarca);
  };

  const addModelUnit = async () => {
    const requiredFields = ["descripcion", "idMarca"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(modelUnitData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t('modelUnit.addOrEdit.requiredField');
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const dataUser = EncryptionUtils.getDecryptedData("user");
    const parsedUser = JSON.parse(dataUser);

    modelUnitData.usuarioRegistro = parsedUser.userId;

    await POST(`${config.url}/api/ModelUnit`, modelUnitData);
    navigate("/ModelosUnidad");
  };

  const editModelUnit = async () => {
    const requiredFields = ["descripcion", "idMarca"];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(modelUnitData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t('modelUnit.addOrEdit.requiredField');
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const dataUser = EncryptionUtils.getDecryptedData("user");
    const parsedUser = JSON.parse(dataUser);

    modelUnitData.usuarioRegistro = parsedUser.userId;

    await PUT(`${config.url}/api/ModelUnit`, modelUnitData);
    navigate("/ModelosUnidad");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setModelUnitData({
      ...modelUnitData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      await editModelUnit();
    } else {
      await addModelUnit();
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/ModelosUnidad"
            tooltip={t('buttons.back')}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {isEditMode ? t('modelUnit.addOrEdit.edit') : t('modelUnit.addOrEdit.add')} {t('modelUnit.addOrEdit.title')}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            marginTop: 2,
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t('modelUnit.addOrEdit.modelInfo')}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="descripcion"
                  name="descripcion"
                  label={t('modelUnit.addOrEdit.description')}
                  variant="standard"
                  onChange={handleChange}
                  value={modelUnitData.descripcion}
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t('modelUnit.addOrEdit.brand')}
                  name="idMarca"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="standard"
                  size="small"
                  value={modelUnitData?.idMarca}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.idMarca}
                  helperText={errors.idMarca}
                >
                  <MenuItem key={0} value={0}>
                    {t('select')}
                  </MenuItem>
                  {marcas.map((marca) => (
                    <MenuItem key={marca.idMarca} value={marca.idMarca}>
                      {marca.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {t('buttons.save')}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
